import { useState, useEffect, useRef } from 'react';
import { studentFetch } from '../utils/authFetch';
import AvatarWizard from '../components/AvatarWizard';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const T = {
  ar: {
    confirmTitle: 'هل تعجبك شخصيتك؟',
    accept:       'أيوه، هذه أنا! ✅',
    retry:        'جرّب مرة أخرى 🔄',
    fallback: [
      'جهبذ شغّال... الفرشاة مش بتمشي! 🎨',
      'قريباً... أظن كده 🤔',
      'أنا جهبذ وهأخلصها! 💪',
      'الألوان بتتعاون معايا النهارده 🌈',
      'شوية صبر... الفن بياخد وقت 🖌️',
      'آخر لمسة وخلصنا يا بطل! ⭐',
    ],
  },
  en: {
    confirmTitle: 'Do you like your character?',
    accept:       "Yes, that's me! ✅",
    retry:        'Try again 🔄',
    fallback: [
      "Jahbath is working... the brush won't cooperate! 🎨",
      'Almost done... I think 🤔',
      "I'm Jahbath and I'll nail this! 💪",
      'The colors are behaving today 🌈',
      'Just a moment... art takes time 🖌️',
      "Final touch and we're done champ! ⭐",
    ],
  },
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --emerald:       #059669;
    --emerald-light: #10B981;
    --emerald-pale:  #D1FAE5;
    --emerald-bg:    #F0FDF4;
    --gold:          #D97706;
    --gold-light:    #F59E0B;
    --text-dark:     #064E3B;
    --text-body:     #1F2937;
    --text-muted:    #6B7280;
    --border:        #E5E7EB;
  }

  .so-root {
    min-height: 100vh;
    background: transparent;
    font-family: 'Sora', sans-serif;
    display: flex; align-items: center; justify-content: center;
    padding: 24px 16px;
    position: relative; overflow: hidden;
  }

  .so-orb1 {
    position: fixed; top: -140px; right: -140px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(5,150,105,.08) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }
  .so-orb2 {
    position: fixed; bottom: -100px; left: -100px;
    width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(217,119,6,.06) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }

  @keyframes so-slideUp {
    from { opacity: 0; transform: translateY(32px) scale(.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes so-floatHat {
    0%, 100% { transform: translateY(0) rotate(-2deg); }
    50%      { transform: translateY(-8px) rotate(2deg); }
  }
  @keyframes so-pulse {
    0%, 100% { transform: scale(1); filter: drop-shadow(0 6px 20px rgba(5,150,105,.25)); }
    50%      { transform: scale(1.08); filter: drop-shadow(0 10px 30px rgba(5,150,105,.4)); }
  }
  @keyframes so-msgFade {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes so-avatarGlow {
    0%, 100% { box-shadow: 0 0 0 4px rgba(217,119,6,.3), 0 0 24px rgba(217,119,6,.2); }
    50%      { box-shadow: 0 0 0 6px rgba(217,119,6,.5), 0 0 40px rgba(217,119,6,.35); }
  }
  @keyframes so-spin {
    to { transform: rotate(360deg); }
  }
  @keyframes so-fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .so-card {
    background: white; border-radius: 32px; padding: 44px 40px;
    width: 100%; max-width: 460px;
    box-shadow: 0 8px 0 rgba(5,150,105,.08), 0 24px 60px rgba(5,150,105,.15);
    border: 2px solid rgba(5,150,105,.15);
    position: relative; z-index: 1;
    animation: so-slideUp .5s cubic-bezier(.22,.68,0,1.2) both;
  }

  .so-logo {
    display: flex; flex-direction: column; align-items: center; margin-bottom: 20px;
  }
  .so-hat {
    height: 62px; width: auto; margin-bottom: 6px;
    filter: drop-shadow(0 6px 16px rgba(5,150,105,.2));
    animation: so-floatHat 3.5s ease-in-out infinite;
  }
  .so-hat.pulsing { animation: so-pulse 2s ease-in-out infinite; }
  .so-brand {
    font-family: 'Sora', sans-serif;
    font-size: 1.7rem; font-weight: 800;
    color: #1E3A5F; letter-spacing: -.02em;
  }
  .so-brand span { color: var(--emerald); }

  .so-content { transition: opacity .25s ease, transform .25s ease; }
  .so-content.hidden { opacity: 0; transform: translateY(8px); }

  .so-title {
    font-family: 'Sora', sans-serif;
    font-size: 1.05rem; font-weight: 800;
    color: var(--text-dark); text-align: center; margin-bottom: 28px;
  }

  .so-btn {
    width: 100%; padding: 15px;
    font-family: 'Sora', sans-serif; font-size: 1rem; font-weight: 700;
    border: none; border-radius: 20px;
    cursor: pointer; transition: transform .08s, box-shadow .08s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .so-btn-gold {
    background: linear-gradient(135deg, var(--gold), var(--gold-light));
    color: white; border: 2px solid #FCD34D;
    box-shadow: 0 5px 0 #FCD34D, 0 8px 20px rgba(217,119,6,.2);
    margin-bottom: 12px;
  }
  .so-btn-gold:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 0 #FCD34D, 0 14px 26px rgba(217,119,6,.3);
  }
  .so-btn-gold:active { transform: translateY(4px); box-shadow: 0 1px 0 #FCD34D; }
  .so-btn-outline {
    background: white; color: var(--text-muted);
    border: 2px solid var(--border); box-shadow: 0 3px 0 #E5E7EB;
    transition: transform .08s, box-shadow .08s, border-color .15s;
  }
  .so-btn-outline:hover {
    border-color: var(--emerald); color: var(--emerald);
    box-shadow: 0 3px 0 #D1FAE5; transform: translateY(-2px);
  }
  .so-btn-outline:active { transform: translateY(3px); box-shadow: 0 1px 0 #E5E7EB; }

  .so-waiting-body {
    display: flex; flex-direction: column; align-items: center;
    padding: 8px 0 16px; gap: 16px;
  }
  .so-wait-spinner {
    width: 36px; height: 36px;
    border: 3.5px solid var(--emerald-pale);
    border-top-color: var(--emerald);
    border-radius: 50%; animation: so-spin .8s linear infinite;
  }
  .so-msg {
    font-family: 'Sora', sans-serif; font-size: .95rem; font-weight: 700;
    color: var(--text-dark); text-align: center; min-height: 48px;
    transition: opacity .35s ease, transform .35s ease;
    animation: so-msgFade .4s ease both;
  }
  .so-msg.hidden { opacity: 0; transform: translateY(6px); }

  .so-avatar-wrap { display: flex; justify-content: center; margin: 8px 0 28px; }
  .so-avatar {
    width: 180px; height: 180px;
    border-radius: 50%; object-fit: cover;
    border: 4px solid var(--gold);
    animation: so-avatarGlow 2.2s ease-in-out infinite, so-fadeIn .5s ease both;
  }

  @media (max-width: 480px) {
    .so-card { padding: 32px 20px; border-radius: 20px; }
    .so-avatar { width: 150px; height: 150px; }
  }
  @media (min-width: 481px) and (max-width: 767px) {
    .so-card { max-width: 480px; }
  }
`;

export default function StudentOnboarding() {
  const [lang,      setLang]      = useState('ar');
  const [screen,    setScreen]    = useState('form');
  const [visible,   setVisible]   = useState(true);

  // stored for retry after wizard submits
  const [favoriteChar,   setFavoriteChar]   = useState('');
  const [characterType,  setCharacterType]  = useState('');
  const [gender,         setGender]         = useState('');
  const [mood,           setMood]           = useState('');

  const [messages,   setMessages]   = useState([]);
  const [msgIndex,   setMsgIndex]   = useState(0);
  const [msgVisible, setMsgVisible] = useState(true);

  const [avatarUrl,  setAvatarUrl]  = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const t = T[lang] || T.ar;

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  useEffect(() => {
    const sd = JSON.parse(localStorage.getItem('student_data') || '{}');
    const l = sd.language || 'ar';
    setLang(l);
  }, []);

  useEffect(() => {
    if (screen !== 'waiting' || messages.length === 0) return;
    const iv = setInterval(() => {
      setMsgVisible(false);
      setTimeout(() => {
        setMsgIndex(prev => (prev + 1) % messages.length);
        setMsgVisible(true);
      }, 380);
    }, 3000);
    return () => clearInterval(iv);
  }, [screen, messages]);

  const goTo = (next) => {
    setVisible(false);
    setTimeout(() => { setScreen(next); setVisible(true); }, 260);
  };

  const doAvatarGenerate = async (token, sd, cn, ct, g, m) => {
    try {
      const res = await studentFetch(`${API}/student/avatar/generate`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({
          student_id:     sd.id,
          character_name: cn,
          character_type: ct || null,
          gender:         g  || null,
          mood:           m  || null,
          grade:          sd.grade,
        }),
      });
      if (!res) return null;
      const data = await res.json();
      return data.avatar_url || null;
    } catch {
      return null;
    }
  };

  const doSave = async (url) => {
    const token = localStorage.getItem('student_token');
    const sd    = JSON.parse(localStorage.getItem('student_data') || '{}');
    try {
      const res = await studentFetch(`${API}/student/onboarding/complete`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({
          student_id:         sd.id,
          persona_name:       favoriteChar,
          favorite_character: favoriteChar,
          grade:              sd.grade,
          avatar_url:         url || null,
        }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('student_data', JSON.stringify({
          ...sd,
          persona_name:       favoriteChar,
          favorite_character: favoriteChar,
          avatar_url:         url || null,
        }));
      }
    } catch {}
    window.location.href = '/student/dashboard';
  };

  const runAvatarFlow = async (cn, ct, g, m) => {
    setMessages([]);
    setMsgIndex(0);
    goTo('waiting');

    const token = localStorage.getItem('student_token');
    const sd    = JSON.parse(localStorage.getItem('student_data') || '{}');

    const avatarPromise = doAvatarGenerate(token, sd, cn, ct, g, m);

    try {
      const res = await studentFetch(`${API}/student/onboarding/start`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ character_name: cn, grade: sd.grade, language: lang }),
      });
      if (!res) return;
      const data = await res.json();
      const msgs = data.messages && data.messages.length > 0 ? data.messages : t.fallback;
      setMessages(msgs);
      setMsgIndex(0);
      setMsgVisible(true);
    } catch {
      setMessages(t.fallback);
      setMsgIndex(0);
      setMsgVisible(true);
    }

    const url = await avatarPromise;
    setAvatarUrl(url);
    if (url) goTo('confirm');
    else await doSave(null);
  };

  const handleWizardSubmit = ({ characterType: ct, gender: g, mood: m, characterName: cn }) => {
    setFavoriteChar(cn);
    setCharacterType(ct);
    setGender(g);
    setMood(m);
    runAvatarFlow(cn, ct, g, m);
  };

  const handleRetry = async () => {
    setRetryCount(prev => prev + 1);
    goTo('waiting');
    const token = localStorage.getItem('student_token');
    const sd    = JSON.parse(localStorage.getItem('student_data') || '{}');
    const url   = await doAvatarGenerate(token, sd, favoriteChar, characterType, gender, mood);
    setAvatarUrl(url);
    if (url) goTo('confirm');
    else await doSave(null);
  };

  return (
    <div className="so-root" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <style>{CSS}</style>

      <div className="so-card">
        <div className="so-logo">
          <img
            src="/jahbath-hat.png"
            alt="Jahbath"
            className={`so-hat${screen === 'waiting' ? ' pulsing' : ''}`}
          />
          <div className="so-brand">Jah<span>bath</span></div>
        </div>

        <div className={`so-content${visible ? '' : ' hidden'}`}>

          {screen === 'form' && (
            <AvatarWizard
              lang={lang}
              onSubmit={handleWizardSubmit}
            />
          )}

          {screen === 'waiting' && (
            <div className="so-waiting-body">
              {messages.length === 0
                ? <div className="so-wait-spinner" />
                : <p className={`so-msg${msgVisible ? '' : ' hidden'}`}>{messages[msgIndex]}</p>
              }
            </div>
          )}

          {screen === 'confirm' && (
            <>
              <div className="so-avatar-wrap">
                <img src={avatarUrl} alt="avatar" className="so-avatar" />
              </div>
              <div className="so-title">{t.confirmTitle}</div>
              <button className="so-btn so-btn-gold" onClick={() => doSave(avatarUrl)}>
                {t.accept}
              </button>
              {retryCount < 3 && (
                <button className="so-btn so-btn-outline" onClick={handleRetry}>
                  {t.retry}
                </button>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}
